import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { RotateCcw, Sparkles, Loader2, Check } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [defaultLimit, setDefaultLimit] = useState(5);
  const [limitDrafts, setLimitDrafts] = useState({});
  const [faceRefs, setFaceRefs] = useState([]);
  const [codes, setCodes] = useState([]);
  const [codeCount, setCodeCount] = useState(1);
  const [codeEmail, setCodeEmail] = useState("");
  const [codeNote, setCodeNote] = useState("");
  const [bootstrapState, setBootstrapState] = useState({ running: false, done: 0, total: 0, failed: 0, current: null });
  const [bootstrapping, setBootstrapping] = useState(false);
  const [rowBootstrapping, setRowBootstrapping] = useState({});
  const pollRef = useRef(null);

  const load = async (query = "") => {
    const [s, u, f, c] = await Promise.all([
      api.get("/admin/stats"),
      api.get(`/admin/users${query ? `?q=${encodeURIComponent(query)}` : ""}`),
      api.get("/face-references"),
      api.get("/admin/access-codes"),
    ]);
    setStats(s.data);
    setUsers(u.data.items);
    setDefaultLimit(s.data.default_monthly_limit);
    setFaceRefs(f.data.items || []);
    setCodes(c.data.items || []);
  };

  const issueCodes = async () => {
    try {
      const { data } = await api.post("/admin/access-codes", {
        count: Number(codeCount) || 1,
        email: codeEmail || null,
        note: codeNote || null,
      });
      toast.success(`${data.items.length} kode dibuat: ${data.items.map((i) => i.code).join(", ")}`);
      setCodeEmail("");
      setCodeNote("");
      const c = await api.get("/admin/access-codes");
      setCodes(c.data.items || []);
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Gagal membuat kode");
    }
  };

  const revokeCode = async (code) => {
    try {
      await api.post(`/admin/access-codes/${encodeURIComponent(code)}/revoke`);
      toast.success("Kode dicabut");
      const c = await api.get("/admin/access-codes");
      setCodes(c.data.items || []);
    } catch {
      toast.error("Gagal mencabut kode");
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Kode disalin");
    } catch {
      toast.error("Gagal menyalin");
    }
  };

  const loadFaceRefs = () =>
    api.get("/face-references").then((r) => setFaceRefs(r.data.items || [])).catch(() => {});

  useEffect(() => {
    load().catch(() => toast.error("Failed to load admin data"));
    // If a bootstrap is already in progress when this page mounts (e.g., user
    // refreshed mid-run), re-attach the poller so we see live progress.
    (async () => {
      try {
        const { data } = await api.get("/face-references/bootstrap-status");
        setBootstrapState(data);
        if (data.running) {
          setBootstrapping(true);
          startPolling();
        }
      } catch {}
    })();
    return () => pollRef.current && clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);

  const toggleUser = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { is_disabled: !u.is_disabled });
      toast.success(`User ${u.is_disabled ? "enabled" : "disabled"}`);
      load(q);
    } catch {
      toast.error("Update failed");
    }
  };

  const saveLimit = async (u) => {
    const raw = limitDrafts[u.user_id];
    const value = raw === "" || raw === undefined ? null : Number(raw);
    if (value !== null && (isNaN(value) || value < 0)) {
      toast.error("Invalid limit");
      return;
    }
    try {
      await api.patch(`/admin/users/${u.user_id}`, {
        monthly_limit_override: value ?? 0,
      });
      toast.success("Limit updated");
      load(q);
    } catch {
      toast.error("Update failed");
    }
  };

  const saveDefault = async () => {
    try {
      await api.put("/admin/settings/default-limit", { value: Number(defaultLimit) });
      toast.success("Default limit updated");
      load(q);
    } catch {
      toast.error("Update failed");
    }
  };

  const resetOverride = async (u) => {
    try {
      await api.delete(`/admin/users/${u.user_id}/override`);
      toast.success("Override cleared");
      load(q);
    } catch {
      toast.error("Reset failed");
    }
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/face-references/bootstrap-status");
        setBootstrapState(data);
        loadFaceRefs();
        if (!data.running) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setBootstrapping(false);
          if (data.done > 0) toast.success(`${data.done} face portrait${data.done === 1 ? "" : "s"} generated.`);
          if (data.failed > 0) toast.error(`${data.failed} portrait${data.failed === 1 ? "" : "s"} failed.`);
        }
      } catch {}
    }, 3500);
  };

  const bootstrapAll = async (force = false) => {
    if (!window.confirm(
      force
        ? `Regenerate ALL ${faceRefs.length} face portraits? This will consume up to ${faceRefs.length} LLM credits.`
        : "Generate portraits for face references that don't have one yet? Each costs 1 LLM credit."
    )) return;
    setBootstrapping(true);
    try {
      const { data } = await api.post(`/face-references/bootstrap-all${force ? "?force=true" : ""}`);
      if (data.already_running) {
        toast.info("A bootstrap is already running — attaching to progress.");
        const { data: status } = await api.get("/face-references/bootstrap-status");
        setBootstrapState(status);
        startPolling();
        return;
      }
      if (data.queued === 0) {
        toast.info(data.message || "Nothing to do.");
        setBootstrapping(false);
        return;
      }
      toast.info(`Queued ${data.queued} face portraits for generation…`);
      const { data: status } = await api.get("/face-references/bootstrap-status");
      setBootstrapState(status);
      startPolling();
    } catch (e) {
      setBootstrapping(false);
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Bootstrap failed");
    }
  };

  const bootstrapOne = async (refId) => {
    setRowBootstrapping((r) => ({ ...r, [refId]: true }));
    try {
      await api.post(`/face-references/${refId}/bootstrap`);
      await loadFaceRefs();
      toast.success(`Regenerated ${refId}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Bootstrap failed");
    } finally {
      setRowBootstrapping((r) => ({ ...r, [refId]: false }));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">Control Panel</p>
      <h1 className="mt-3 font-serif text-4xl sm:text-5xl leading-none tracking-tight text-zinc-950">
        Admin
      </h1>

      {stats && (
        <div data-testid={ADMIN.stats} className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total users" value={stats.total_users} />
          <StatCard label="Lifetime subscribers" value={stats.lifetime_subscribers ?? 0} />
          <StatCard label="Gratis harian" value={stats.gratis_harian ?? 0} />
          <StatCard label="Total renders" value={stats.total_generations} />
        </div>
      )}

      <FaceRefManager
        faceRefs={faceRefs}
        bootstrapAll={bootstrapAll}
        bootstrapOne={bootstrapOne}
        bootstrapping={bootstrapping}
        rowBootstrapping={rowBootstrapping}
        bootstrapState={bootstrapState}
      />

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl tracking-tight">Users</h2>
          <input
            data-testid={ADMIN.search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email or name…"
            className="w-72 border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2 text-sm bg-white"
          />
        </div>
        <div
          data-testid={ADMIN.usersTable}
          className="border border-zinc-200 bg-white overflow-x-auto"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200">
                <th className="p-4">User</th>
                <th className="p-4">Status</th>
                <th className="p-4">Enabled</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-zinc-100 last:border-0">
                  <td className="p-4">
                    <p className="font-medium text-zinc-950">{u.name}</p>
                    <p className="text-[12px] text-zinc-500">{u.email}</p>
                    {u.is_admin && (
                      <span className="mt-1 inline-block font-mono text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]">
                        Admin
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.15em] border ${u.status_langganan === "Lifetime" ? "border-zinc-900 bg-zinc-950 text-[#FBFBF9]" : "border-zinc-300 bg-white text-zinc-600"}`}>
                      {u.status_langganan}
                    </span>
                    <p className="mt-1.5 text-[11px] font-mono uppercase tracking-[0.15em] text-zinc-400">
                      {u.license_activated_at ? "Activated" : "No code"}
                    </p>
                  </td>
                  <td className="p-4">
                    <Switch
                      data-testid={`${ADMIN.enableSwitch}-${u.user_id}`}
                      checked={!u.is_disabled}
                      onCheckedChange={() => toggleUser(u)}
                    />
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={3} className="p-8 text-center text-zinc-500 text-sm">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-serif text-2xl tracking-tight">Activation codes</h2>
            <p className="mt-1 text-[13px] text-zinc-500">
              Satu kode = satu akun. Kirim link web + kode ke pembeli setelah pembayaran.
              Kode juga otomatis dibuat oleh webhook Scalev.
            </p>
          </div>
        </div>

        <div className="border border-zinc-200 bg-white p-5">
          <div className="grid gap-3 sm:grid-cols-4">
            <label className="text-[12px] text-zinc-600">
              Jumlah
              <input
                type="number"
                min={1}
                max={50}
                value={codeCount}
                onChange={(e) => setCodeCount(e.target.value)}
                className="mt-1 w-full border border-zinc-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="text-[12px] text-zinc-600">
              Email (opsional, mengunci kode)
              <input
                value={codeEmail}
                onChange={(e) => setCodeEmail(e.target.value)}
                placeholder="pembeli@email.com"
                className="mt-1 w-full border border-zinc-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="text-[12px] text-zinc-600">
              Catatan
              <input
                value={codeNote}
                onChange={(e) => setCodeNote(e.target.value)}
                placeholder="mis. Order Scalev #123"
                className="mt-1 w-full border border-zinc-300 px-3 py-2 text-sm"
              />
            </label>
            <div className="flex items-end">
              <button
                type="button"
                onClick={issueCodes}
                className="w-full bg-zinc-950 px-4 py-2 text-sm text-[#FBFBF9] hover:bg-zinc-800"
              >
                Buat kode
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200">
                <th className="p-4">Kode</th>
                <th className="p-4">Status</th>
                <th className="p-4">Email</th>
                <th className="p-4">Pakai</th>
                <th className="p-4" />
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.code} className="border-b border-zinc-100 last:border-0">
                  <td className="p-4 font-mono text-[13px] tracking-wider">{c.code}</td>
                  <td className="p-4">
                    <span
                      className={`inline-flex px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.15em] border ${
                        c.status === "open"
                          ? "border-emerald-600 text-emerald-700"
                          : c.status === "used"
                            ? "border-zinc-900 bg-zinc-950 text-[#FBFBF9]"
                            : "border-rose-300 text-rose-600"
                      }`}
                    >
                      {c.status}
                    </span>
                  </td>
                  <td className="p-4 text-[12px] text-zinc-500">{c.email || "—"}</td>
                  <td className="p-4 text-[12px] text-zinc-500">
                    {c.used_email || "—"}
                    {c.source === "scalev" && (
                      <span className="ml-2 font-mono text-[10px] uppercase text-zinc-400">
                        scalev
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => copy(c.code)}
                        className="border border-zinc-300 px-3 py-1 text-[12px] hover:border-zinc-900"
                      >
                        Copy
                      </button>
                      {c.status !== "used" && c.status !== "revoked" && (
                        <button
                          type="button"
                          onClick={() => revokeCode(c.code)}
                          className="border border-rose-300 px-3 py-1 text-[12px] text-rose-600 hover:border-rose-600"
                        >
                          Cabut
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {codes.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-zinc-500 text-sm">
                    Belum ada kode. Buat kode pertama di atas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="border border-zinc-200 p-5 bg-white">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</p>
      <p className="mt-2 font-mono text-3xl tabular-nums text-zinc-950">{value}</p>
    </div>
  );
}

function FaceRefManager({ faceRefs, bootstrapAll, bootstrapOne, bootstrapping, rowBootstrapping, bootstrapState }) {
  const withThumb = faceRefs.filter((r) => r.thumbnail_path).length;
  const total = faceRefs.length;
  const progressPct =
    bootstrapState.total > 0
      ? Math.round(((bootstrapState.done + bootstrapState.failed) / bootstrapState.total) * 100)
      : 0;

  return (
    <div className="mt-8" data-testid={ADMIN.faceRefSection}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
        <div>
          <h2 className="font-serif text-2xl tracking-tight">Face Reference Library</h2>
          <p className="mt-1 text-[13px] text-zinc-500">
            AI-generated portraits for the {total} fictional models across European, East Asian, South Asian, Black, Latinx and Middle Eastern profiles. {withThumb}/{total} portraits ready.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={ADMIN.faceRefBootstrapAll}
            onClick={() => bootstrapAll(false)}
            disabled={bootstrapping || withThumb === total}
            className="inline-flex items-center gap-2 bg-zinc-950 text-[#FBFBF9] px-4 py-2 text-sm hover:bg-zinc-800 disabled:bg-zinc-400 disabled:cursor-not-allowed transition-colors"
          >
            {bootstrapping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {bootstrapping ? "Generating…" : "Bootstrap all"}
          </button>
          <button
            data-testid={ADMIN.faceRefBootstrapForce}
            onClick={() => bootstrapAll(true)}
            disabled={bootstrapping}
            title={`Regenerate every portrait (costs ${total} credits)`}
            className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-700 px-3 py-2 text-sm hover:border-zinc-900 disabled:opacity-50 transition-colors"
          >
            Force re-run all
          </button>
        </div>
      </div>

      {bootstrapState.running && (
        <div data-testid={ADMIN.faceRefProgress} className="mb-4 border border-zinc-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Generating {bootstrapState.current || "…"} — {bootstrapState.done + bootstrapState.failed}/{bootstrapState.total}
            </span>
            <span className="font-mono text-[10px] tabular-nums text-zinc-500">{progressPct}%</span>
          </div>
          <div className="mt-2 h-[2px] bg-zinc-100">
            <div className="h-full bg-zinc-950 transition-[width]" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {faceRefs.map((r) => {
          const isCurrent = bootstrapState.running && bootstrapState.current === r.ref_id;
          const isLoading = rowBootstrapping[r.ref_id] || isCurrent;
          return (
            <div
              key={r.ref_id}
              data-testid={`${ADMIN.faceRefBootstrapCard}-${r.ref_id}`}
              className="border border-zinc-200 bg-white overflow-hidden"
            >
              <div className="relative aspect-[3/4] bg-zinc-100">
                {r.thumbnail_path ? (
                  <AuthImage width={240} path={r.thumbnail_path} alt={r.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-zinc-100 via-white to-zinc-100 flex flex-col items-center justify-center p-2 text-center">
                    <span className="font-serif text-4xl text-zinc-950 leading-none">{r.name.charAt(0)}</span>
                    <span className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                      No portrait
                    </span>
                  </div>
                )}
                {r.thumbnail_path && (
                  <span className="absolute top-1.5 right-1.5 h-5 w-5 bg-zinc-950/90 text-[#FBFBF9] flex items-center justify-center">
                    <Check className="h-3 w-3" />
                  </span>
                )}
                {isLoading && (
                  <div className="absolute inset-0 bg-zinc-950/70 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-[#FBFBF9]" />
                  </div>
                )}
              </div>
              <div className="p-2.5">
                <p className="font-serif text-[15px] text-zinc-950 leading-tight">{r.name}</p>
                <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500 leading-snug">
                  {r.descriptor}
                </p>
                <button
                  data-testid={`${ADMIN.faceRefBootstrapOne}-${r.ref_id}`}
                  onClick={() => bootstrapOne(r.ref_id)}
                  disabled={bootstrapping || isLoading}
                  className="mt-2 w-full text-[11px] font-mono uppercase tracking-[0.15em] border border-zinc-300 py-1.5 hover:border-zinc-900 disabled:opacity-50 transition-colors"
                >
                  {r.thumbnail_path ? "Regenerate" : "Generate"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
